import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Form } from 'semantic-ui-react'
import {
    CustomDropdown,
    FormDropdownField,
    FormTextAreaFormField,
    FormTextFormField,
} from 'elements'
import { useNotification } from 'hooks'
import {
    buildWeekRowsForMonth,
    formatWeekDateRangeLabel,
    isWeeklyRotationDetail,
    matchRotationDetailsToWeekRows,
    parseRotationTemplateIds,
} from 'utility'
import { FormMonthYearField } from './FormMonthYearField'
import {
    RotationFooterCard,
    RotationStepBody,
    RotationStepCard,
    RotationStepHeader,
    RotationStepSubtitle,
    RotationStepTitle,
} from './rotation-section-styles'
import {
    BtnGhost,
    BtnPrimary,
    BtnSecondary,
    ChangeFlag,
    CopyBtn,
    CustomRangeForm,
    EntryStep,
    EntryStepSep,
    EntryStepper,
    PartialBadge,
    PreviewStrip,
    PreviewWk,
    QuickActions,
    SaveBtnRow,
    SwitchRow,
    ToggleSwitch,
    ValidationStrip,
    WeekDropdownCell,
    WeekGridWrap,
    QuickFillCell,
    WkDate,
    WkDropdownCol,
    WkNum,
} from './weekly-rotation-entry-styles'

const EMAIL_NOTIFY_OPTIONS = [
    { text: 'Yes — send email per week', value: 'per_week' },
    { text: 'Once — single summary email', value: 'once' },
    { text: 'No emails', value: 'none' },
]

const emptyAssignment = () => ({
    workPlaceId: '',
    templateIds: [],
})

const mergeWeekRows = (baseRows, prevAssignments) => {
    const prevMap = new Map(
        (prevAssignments || []).map(row => [row.key, row]),
    )
    return baseRows.map(row => {
        const prev = prevMap.get(row.key)
        return {
            ...row,
            assignment: prev?.assignment || emptyAssignment(),
        }
    })
}

const buildWeekRowsWithDetails = (baseRows, matchedDetails) =>
    baseRows.map((row, index) => {
        const detail = matchedDetails[index]
        return {
            ...row,
            assignment: detail
                ? {
                      workPlaceId: detail.WorkPlaceId || '',
                      templateIds: parseRotationTemplateIds(
                          detail.TemplateId,
                      ),
                  }
                : emptyAssignment(),
        }
    })

const detailToCustomRow = detail => {
    const startIso = String(detail.FromDate || '').slice(0, 10)
    const endIso = String(detail.ToDate || detail.FromDate || '').slice(0, 10)
    if (!startIso || !endIso) return null

    const start = new Date(`${startIso}T00:00:00`)
    const end = new Date(`${endIso}T00:00:00`)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null

    return {
        key: `custom-${detail.RotationDetailId ?? `${startIso}-${endIso}`}`,
        weekOfMonth: null,
        isCustom: true,
        startDate: startIso,
        endDate: endIso,
        sunday: start,
        saturday: end,
        isPartial:
            detail.DurationDays != null && Number(detail.DurationDays) < 7,
        assignment: {
            workPlaceId: detail.WorkPlaceId || '',
            templateIds: parseRotationTemplateIds(detail.TemplateId),
        },
    }
}

const sortWeekRows = rows =>
    [...rows].sort(
        (a, b) =>
            new Date(`${a.startDate}T00:00:00`).getTime() -
            new Date(`${b.startDate}T00:00:00`).getTime(),
    )

const findOptionText = (options, value) =>
    options.find(o => String(o.value) === String(value))?.text || '—'

const findOptionsText = (options, values) => {
    let ids = []
    if (Array.isArray(values)) {
        ids = values
    } else if (values != null && values !== '') {
        ids = [values]
    }
    if (!ids.length) return '—'
    const labels = ids
        .map(id => findOptionText(options, id))
        .filter(text => text !== '—')
    return labels.length ? labels.join(', ') : '—'
}

const templateIdsEqual = (a, b) =>
    JSON.stringify([...(a || [])].map(String).sort()) ===
    JSON.stringify([...(b || [])].map(String).sort())

const resolveSendEmailForWeek = (mode, weekIndex) => {
    if (mode === 'none') return 0
    if (mode === 'once') return weekIndex === 0 ? 1 : 0
    return 1
}

function WeekGridToolbar({
    onFillAll,
    onClearAll,
    showCustomRange,
    onToggleCustomRange,
    customStart,
    customEnd,
    onCustomStartChange,
    onCustomEndChange,
    onAddCustomRange,
    onCancelCustomRange,
}) {
    return (
        <>
            <QuickActions>
                <div className="toolbar-actions">
                    <button type="button" onClick={onFillAll}>
                        Fill all weeks from Week 1
                    </button>
                    <button type="button" onClick={onClearAll}>
                        Clear all
                    </button>
                    <button type="button" onClick={onToggleCustomRange}>
                        + Add custom date range
                    </button>
                </div>
                <span className="quick-tip">
                    Tip: only filled weeks are saved — leave rows empty to omit
                    them. Use Copy ↓ to repeat values row to row.
                </span>
            </QuickActions>
            {showCustomRange ? (
                <CustomRangeForm>
                    <label>
                        Start date
                        <input
                            type="date"
                            value={customStart}
                            onChange={onCustomStartChange}
                        />
                    </label>
                    <label>
                        End date
                        <input
                            type="date"
                            value={customEnd}
                            onChange={onCustomEndChange}
                        />
                    </label>
                    <button type="button" onClick={onAddCustomRange}>
                        Add row
                    </button>
                    <button type="button" onClick={onCancelCustomRange}>
                        Cancel
                    </button>
                </CustomRangeForm>
            ) : null}
        </>
    )
}

const renderQuickFillButton = (idx, handlers) => {
    if (idx === 0) {
        return (
            <CopyBtn
                type="button"
                onClick={() => handlers.copyFrom(0, true)}
            >
                Copy ↓ all
            </CopyBtn>
        )
    }
    return (
        <CopyBtn
            type="button"
            onClick={() => handlers.copyFrom(idx, false)}
        >
            Copy ↓
        </CopyBtn>
    )
}

export const WeeklyRotationEntryForm = forwardRef(
    (
        {
            control,
            errors,
            watch,
            setValue,
            shifts,
            employees,
            workPlaces,
            templates,
            lookupLoading,
            loading,
            onSave,
            onSaveDraft,
            onCancel,
        },
        ref,
    ) => {
        const [sameAuditor, setSameAuditor] = useState(true)
        const [sameShift, setSameShift] = useState(true)
        const [sameWorkplace, setSameWorkplace] = useState(false)
        const [sameTemplate, setSameTemplate] = useState(false)
        const [weekRows, setWeekRows] = useState([])
        const [showCustomRange, setShowCustomRange] = useState(false)
        const [customStart, setCustomStart] = useState('')
        const [customEnd, setCustomEnd] = useState('')

        const pendingDetailsLoadRef = useRef(null)

        const { errorToast } = useNotification()

        const scheduleMonth = watch('ScheduleMonth')
        const scheduleYear = watch('ScheduleYear')
        const title = watch('Title')
        const employeeId = watch('EmployeeId')
        const shiftId = watch('ShiftId')
        const workPlaceId = watch('WorkPlaceId')
        const sharedTemplateIds = watch('SharedTemplateIds') || []
        const emailNotifyMode = watch('EmailNotifyMode') || 'per_week'

        const monthLabel = useMemo(() => {
            if (!scheduleMonth || !scheduleYear) return ''
            return new Date(scheduleYear, scheduleMonth - 1, 1).toLocaleDateString(
                'en-US',
                { month: 'long', year: 'numeric' },
            )
        }, [scheduleMonth, scheduleYear])

        const applyDetailsToGrid = useCallback((details, month, year) => {
            const baseRows = buildWeekRowsForMonth(year, month)
            const sorted = [...details].sort(
                (a, b) =>
                    new Date(a.FromDate).getTime() -
                    new Date(b.FromDate).getTime(),
            )
            const weeklyDetails = sorted.filter(d => isWeeklyRotationDetail(d))
            const customDetails = sorted.filter(d => !isWeeklyRotationDetail(d))
            const matched = matchRotationDetailsToWeekRows(
                baseRows,
                weeklyDetails,
            )
            const standardRows = buildWeekRowsWithDetails(baseRows, matched)
            const customRows = customDetails
                .map(detail => detailToCustomRow(detail))
                .filter(Boolean)

            setWeekRows(sortWeekRows([...standardRows, ...customRows]))
        }, [])

        const resetGrid = useCallback((month, year) => {
            pendingDetailsLoadRef.current = null
            setShowCustomRange(false)
            setCustomStart('')
            setCustomEnd('')
            if (!month || !year) {
                setWeekRows([])
                return
            }
            setWeekRows(
                sortWeekRows(
                    buildWeekRowsForMonth(year, month).map(row => ({
                        ...row,
                        assignment: emptyAssignment(),
                    })),
                ),
            )
        }, [])

        useEffect(() => {
            if (!scheduleMonth || !scheduleYear) return

            const pending = pendingDetailsLoadRef.current
            if (
                pending &&
                Number(pending.month) === Number(scheduleMonth) &&
                Number(pending.year) === Number(scheduleYear)
            ) {
                pendingDetailsLoadRef.current = null
                applyDetailsToGrid(
                    pending.details,
                    scheduleMonth,
                    scheduleYear,
                )
                return
            }

            setWeekRows(prev => {
                const standard = mergeWeekRows(
                    buildWeekRowsForMonth(scheduleYear, scheduleMonth),
                    prev.filter(row => !row.isCustom),
                )
                return sortWeekRows(standard)
            })
        }, [scheduleMonth, scheduleYear, applyDetailsToGrid])

        const resolveWorkPlaceId = useCallback(
            row =>
                sameWorkplace ? workPlaceId : row.assignment.workPlaceId,
            [sameWorkplace, workPlaceId],
        )

        const resolveTemplateIds = useCallback(
            row =>
                sameTemplate ? sharedTemplateIds : row.assignment.templateIds,
            [sameTemplate, sharedTemplateIds],
        )

        const isWeekRowComplete = useCallback(
            row => {
                const wp = resolveWorkPlaceId(row)
                const tplIds = resolveTemplateIds(row)
                return (
                    wp != null &&
                    wp !== '' &&
                    Array.isArray(tplIds) &&
                    tplIds.length > 0
                )
            },
            [resolveWorkPlaceId, resolveTemplateIds],
        )

        const weeksToSave = useMemo(
            () => weekRows.filter(isWeekRowComplete),
            [weekRows, isWeekRowComplete],
        )

        const validation = useMemo(() => {
            const issues = []
            if (!title?.trim()) issues.push('Schedule title is required.')

            if (sameAuditor && !employeeId) {
                issues.push('Select an auditor.')
            }
            if (sameShift && !shiftId) issues.push('Select a shift.')
            if (sameWorkplace && !workPlaceId) {
                issues.push('Select a workplace.')
            }
            if (sameTemplate && (!sharedTemplateIds || sharedTemplateIds.length === 0)) {
                issues.push('Select at least one audit template.')
            }

            if (weeksToSave.length === 0) {
                issues.push(
                    'Fill at least one week with a workplace and template (empty weeks are not saved).',
                )
            }

            return {
                ok: issues.length === 0,
                issues,
            }
        }, [
            title,
            sameAuditor,
            employeeId,
            sameShift,
            shiftId,
            sameWorkplace,
            workPlaceId,
            sameTemplate,
            sharedTemplateIds,
            weeksToSave.length,
        ])

        const emailSummary = useMemo(() => {
            if (emailNotifyMode === 'none') return 'No emails will be sent.'
            const count = weeksToSave.length
            const auditorName = findOptionText(employees, employeeId)
            if (emailNotifyMode === 'once') {
                return `${auditorName} will receive 1 summary email.`
            }
            return `${auditorName} will receive ${count} email${
                count === 1 ? '' : 's'
            } (one per week).`
        }, [emailNotifyMode, weeksToSave.length, employees, employeeId])

        const handleToggleSame = (field, current, setter) => {
            if (!current) {
                const differing = weekRows.some((row, idx) => {
                    if (idx === 0) return false
                    if (field === 'workPlace') {
                        return (
                            row.assignment.workPlaceId !==
                            weekRows[0].assignment.workPlaceId
                        )
                    }
                    if (field === 'template') {
                        return !templateIdsEqual(
                            row.assignment.templateIds,
                            weekRows[0].assignment.templateIds,
                        )
                    }
                    return false
                })
                if (
                    differing &&
                    // eslint-disable-next-line no-alert
                    !window.confirm(
                        'This will overwrite values in later weeks. Continue?',
                    )
                ) {
                    return
                }
                if (field === 'workPlace' && weekRows[0]?.assignment.workPlaceId) {
                    setValue('WorkPlaceId', weekRows[0].assignment.workPlaceId)
                }
                if (
                    field === 'template' &&
                    weekRows[0]?.assignment.templateIds?.length
                ) {
                    setValue(
                        'SharedTemplateIds',
                        weekRows[0].assignment.templateIds,
                    )
                }
            }
            setter(!current)
        }

        const updateAssignment = (key, field, value) => {
            setWeekRows(prev =>
                prev.map(row =>
                    row.key === key
                        ? {
                              ...row,
                              assignment: { ...row.assignment, [field]: value },
                          }
                        : row,
                ),
            )
        }

        const copyFromRow = (sourceIdx, allFollowing = false) => {
            const source = weekRows[sourceIdx]
            if (!source) return
            setWeekRows(prev =>
                prev.map((row, idx) => {
                    if (allFollowing) {
                        if (idx <= sourceIdx) return row
                    } else if (idx !== sourceIdx + 1) {
                        return row
                    }
                    const nextAssignment = { ...row.assignment }
                    if (!sameWorkplace) {
                        nextAssignment.workPlaceId =
                            source.assignment.workPlaceId
                    }
                    if (!sameTemplate) {
                        nextAssignment.templateIds = [
                            ...(source.assignment.templateIds || []),
                        ]
                    }
                    return { ...row, assignment: nextAssignment }
                }),
            )
        }

        const fillAllFromWeek1 = () => copyFromRow(0, true)

        const clearAllAssignments = () => {
            setWeekRows(prev =>
                prev.map(row => ({
                    ...row,
                    assignment: emptyAssignment(),
                })),
            )
            setValue('WorkPlaceId', '')
            setValue('SharedTemplateIds', [])
            setShowCustomRange(false)
            setCustomStart('')
            setCustomEnd('')
        }

        const removeCustomRow = key => {
            setWeekRows(prev => prev.filter(row => row.key !== key))
        }

        const handleAddCustomDateRange = () => {
            if (!customStart || !customEnd) {
                errorToast('Enter both start and end dates.')
                return
            }
            const start = new Date(`${customStart}T00:00:00`)
            const end = new Date(`${customEnd}T00:00:00`)
            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                errorToast('Enter valid dates.')
                return
            }
            if (end < start) {
                errorToast('End date must be on or after start date.')
                return
            }

            const customRow = {
                key: `custom-${Date.now()}`,
                weekOfMonth: null,
                isCustom: true,
                startDate: customStart,
                endDate: customEnd,
                sunday: start,
                saturday: end,
                isPartial: false,
                assignment: emptyAssignment(),
            }

            setWeekRows(prev => sortWeekRows([...prev, customRow]))
            setShowCustomRange(false)
            setCustomStart('')
            setCustomEnd('')
        }

        const getSavePayload = useCallback(
            (isDraft = false) => ({
                isDraft,
                sameAuditor,
                sameShift,
                sameWorkplace,
                sameTemplate,
                emailNotifyMode,
                weeks: weeksToSave.map((row, idx) => ({
                    ...row,
                    workPlaceId: resolveWorkPlaceId(row),
                    templateIds: resolveTemplateIds(row),
                    sendEmail: resolveSendEmailForWeek(emailNotifyMode, idx),
                })),
            }),
            [
                weeksToSave,
                sameAuditor,
                sameShift,
                sameWorkplace,
                sameTemplate,
                emailNotifyMode,
                resolveWorkPlaceId,
                resolveTemplateIds,
            ],
        )

        const loadFromDetails = useCallback(
            (details, month, year) => {
                if (!details?.length || !month || !year) return

                const sorted = [...details].sort(
                    (a, b) =>
                        new Date(a.FromDate).getTime() -
                        new Date(b.FromDate).getTime(),
                )

                const first = sorted[0]
                if (first?.EmployeeId != null) {
                    setValue('EmployeeId', first.EmployeeId)
                }
                if (first?.ShiftId != null) setValue('ShiftId', first.ShiftId)

                const allSameWorkplace = sorted.every(
                    d => d.WorkPlaceId === first?.WorkPlaceId,
                )
                const allSameTemplate = sorted.every(
                    d => String(d.TemplateId || '') === String(first?.TemplateId || ''),
                )

                setSameAuditor(true)
                setSameShift(true)
                setSameWorkplace(allSameWorkplace)
                setSameTemplate(allSameTemplate)

                if (allSameWorkplace && first?.WorkPlaceId != null) {
                    setValue('WorkPlaceId', first.WorkPlaceId)
                }
                if (allSameTemplate && first?.TemplateId != null) {
                    setValue(
                        'SharedTemplateIds',
                        parseRotationTemplateIds(first.TemplateId),
                    )
                }

                const sendValues = sorted.map(d =>
                    d.SendEmail === true || d.SendEmail === 1 ? 1 : 0,
                )
                if (sendValues.every(v => v === 0)) {
                    setValue('EmailNotifyMode', 'none')
                } else if (sendValues.filter(v => v === 1).length === 1) {
                    setValue('EmailNotifyMode', 'once')
                } else {
                    setValue('EmailNotifyMode', 'per_week')
                }

                pendingDetailsLoadRef.current = {
                    details: sorted,
                    month,
                    year,
                }

                if (
                    Number(scheduleMonth) === Number(month) &&
                    Number(scheduleYear) === Number(year)
                ) {
                    pendingDetailsLoadRef.current = null
                    applyDetailsToGrid(sorted, month, year)
                } else {
                    setValue('ScheduleMonth', month)
                    setValue('ScheduleYear', year)
                }
            },
            [setValue, scheduleMonth, scheduleYear, applyDetailsToGrid],
        )

        useImperativeHandle(ref, () => ({
            getSavePayload,
            loadFromDetails,
            resetGrid,
        }))

        const showWorkplaceCol = !sameWorkplace
        const showTemplateCol = !sameTemplate

        return (
            <>
                <EntryStepper>
                    <EntryStep $done>
                        <span className="n">✓</span>
                        <span>1. Setup</span>
                    </EntryStep>
                    <EntryStepSep />
                    <EntryStep $active>
                        <span className="n">2</span>
                        <span>Weekly assignments</span>
                    </EntryStep>
                    <EntryStepSep />
                    <EntryStep>
                        <span className="n">3</span>
                        <span>Review &amp; save</span>
                    </EntryStep>
                </EntryStepper>

                <RotationStepCard>
                    <RotationStepHeader>
                        <RotationStepTitle>Setup</RotationStepTitle>
                        <RotationStepSubtitle>
                            Basic info — what month, who&apos;s auditing, and what
                            auto-fills you want.
                        </RotationStepSubtitle>
                    </RotationStepHeader>
                    <RotationStepBody>
                        <Form.Group widths="equal">
                            <FormTextFormField
                                control={control}
                                label="Schedule title"
                                name="Title"
                                placeholder="Monthly Rotation - June 2026"
                                errors={errors?.Title}
                                required
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormMonthYearField
                                control={control}
                                label="Month & Year"
                                errors={errors}
                            />
                        </Form.Group>

                        <h4 style={{ margin: '18px 0 8px', fontSize: '15px' }}>
                            Smart defaults
                        </h4>
                        <p
                            style={{
                                fontSize: '12.5px',
                                color: '#6b7280',
                                margin: '0 0 8px',
                            }}
                        >
                            Toggle ON when the same value applies all month.
                        </p>

                        <SwitchRow>
                            <div className="label">
                                Same <b>auditor</b> for the entire month
                            </div>
                            <ToggleSwitch
                                type="button"
                                $on={sameAuditor}
                                aria-pressed={sameAuditor}
                                onClick={() => setSameAuditor(v => !v)}
                            />
                        </SwitchRow>
                        {sameAuditor && (
                            <Form.Group widths="equal">
                                <FormDropdownField
                                    control={control}
                                    label="Auditor"
                                    name="EmployeeId"
                                    placeholder="Select auditor"
                                    errors={errors?.EmployeeId}
                                    options={employees}
                                    loading={lookupLoading}
                                    search
                                    floating
                                />
                            </Form.Group>
                        )}

                        <SwitchRow>
                            <div className="label">
                                Same <b>shift</b> for the entire month
                            </div>
                            <ToggleSwitch
                                type="button"
                                $on={sameShift}
                                aria-pressed={sameShift}
                                onClick={() => setSameShift(v => !v)}
                            />
                        </SwitchRow>
                        {sameShift && (
                            <Form.Group widths="equal">
                                <FormDropdownField
                                    control={control}
                                    label="Shift"
                                    name="ShiftId"
                                    placeholder="Select shift"
                                    errors={errors?.ShiftId}
                                    options={shifts}
                                    loading={lookupLoading}
                                    search
                                    floating
                                />
                            </Form.Group>
                        )}

                        <SwitchRow>
                            <div className="label">
                                Same <b>workplace</b> for the entire month{' '}
                                <span className="hint-inline">
                                    — turn OFF to pick a different workplace per
                                    week
                                </span>
                            </div>
                            <ToggleSwitch
                                type="button"
                                $on={sameWorkplace}
                                aria-pressed={sameWorkplace}
                                onClick={() =>
                                    handleToggleSame(
                                        'workPlace',
                                        sameWorkplace,
                                        setSameWorkplace,
                                    )
                                }
                            />
                        </SwitchRow>
                        {sameWorkplace && (
                            <Form.Group widths="equal">
                                <FormDropdownField
                                    control={control}
                                    label="Work Place"
                                    name="WorkPlaceId"
                                    placeholder="Select work place"
                                    errors={errors?.WorkPlaceId}
                                    options={workPlaces}
                                    loading={lookupLoading}
                                    search
                                    floating
                                />
                            </Form.Group>
                        )}

                        <SwitchRow>
                            <div className="label">
                                Same <b>audit template</b> for every week{' '}
                                <span className="hint-inline">
                                    — turn OFF to pick a different template per
                                    week
                                </span>
                            </div>
                            <ToggleSwitch
                                type="button"
                                $on={sameTemplate}
                                aria-pressed={sameTemplate}
                                onClick={() =>
                                    handleToggleSame(
                                        'template',
                                        sameTemplate,
                                        setSameTemplate,
                                    )
                                }
                            />
                        </SwitchRow>
                        {sameTemplate && (
                            <Form.Group widths="equal">
                                <FormDropdownField
                                    control={control}
                                    label="Templates"
                                    name="SharedTemplateIds"
                                    placeholder="Select templates"
                                    errors={errors?.SharedTemplateIds}
                                    options={templates}
                                    loading={lookupLoading}
                                    search
                                    multiple
                                    floating
                                />
                            </Form.Group>
                        )}

                        <Form.Group widths="equal">
                            <FormDropdownField
                                control={control}
                                label="Notify auditor by email when assigned"
                                name="EmailNotifyMode"
                                placeholder="Select email option"
                                errors={errors?.EmailNotifyMode}
                                options={EMAIL_NOTIFY_OPTIONS}
                                floating
                            />
                            <FormTextAreaFormField
                                control={control}
                                label="Notes (optional)"
                                name="Comments"
                                placeholder="Internal notes about this rotation..."
                                errors={errors?.Comments}
                            />
                        </Form.Group>
                    </RotationStepBody>
                </RotationStepCard>

                <RotationStepCard>
                    <RotationStepHeader>
                        <div
                            className="weekly-assignments-header"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: '12px',
                                flexWrap: 'wrap',
                            }}
                        >
                            <div>
                                <RotationStepTitle>Weekly assignments</RotationStepTitle>
                                <RotationStepSubtitle>
                                    One row per week. Only rows with a workplace
                                    and template are saved — leave others empty.
                                    Use &quot;Copy down&quot; to repeat values.
                                </RotationStepSubtitle>
                            </div>
                            {monthLabel && (
                                <div style={{ fontSize: '13px', color: '#52606d' }}>
                                    {monthLabel} · <b>{weekRows.length} weeks</b>
                                </div>
                            )}
                        </div>
                    </RotationStepHeader>
                    <RotationStepBody>
                        <WeekGridWrap>
                            <table>
                                <colgroup>
                                    <col className="wk-col-week" />
                                    <col className="wk-col-date" />
                                    {showWorkplaceCol && (
                                        <col className="wk-col-dropdown" />
                                    )}
                                    {showTemplateCol && (
                                        <col className="wk-col-dropdown" />
                                    )}
                                    <col className="wk-col-quick" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className="wk-col-week">Week</th>
                                        <th className="wk-col-date">Dates</th>
                                        {showWorkplaceCol && (
                                            <th className="wk-col-dropdown">
                                                Workplace
                                            </th>
                                        )}
                                        {showTemplateCol && (
                                            <th className="wk-col-dropdown">
                                                Templates
                                            </th>
                                        )}
                                        <th className="wk-quick-fill">
                                            Quick fill
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {weekRows.map((row, idx) => {
                                        const prev = idx > 0 ? weekRows[idx - 1] : null
                                        const wpChanged =
                                            showWorkplaceCol &&
                                            prev &&
                                            row.assignment.workPlaceId &&
                                            prev.assignment.workPlaceId &&
                                            row.assignment.workPlaceId !==
                                                prev.assignment.workPlaceId

                                        return (
                                            <tr key={row.key} className="wk-mobile-row">
                                                <WkNum className="wk-col-week">
                                                    <span className="wk-label">
                                                        {row.isCustom
                                                            ? 'Custom'
                                                            : `Week ${row.weekOfMonth}`}
                                                    </span>
                                                    {row.isPartial && (
                                                        <PartialBadge>
                                                            partial
                                                        </PartialBadge>
                                                    )}
                                                </WkNum>
                                                <WkDate className="wk-col-date">
                                                    {formatWeekDateRangeLabel(
                                                        row.startDate,
                                                        row.endDate,
                                                    )}
                                                </WkDate>
                                                {showWorkplaceCol && (
                                                    <WkDropdownCol className="wk-col-dropdown wk-col-dropdown-workplace">
                                                        <WeekDropdownCell
                                                            $changed={wpChanged}
                                                        >
                                                            <CustomDropdown
                                                                className="wk-grid-dropdown"
                                                                selection
                                                                search
                                                                selectOnBlur={false}
                                                                options={workPlaces}
                                                                value={
                                                                    row.assignment
                                                                        .workPlaceId || ''
                                                                }
                                                                placeholder="Select"
                                                                onChange={(
                                                                    _,
                                                                    { value },
                                                                ) =>
                                                                    updateAssignment(
                                                                        row.key,
                                                                        'workPlaceId',
                                                                        value,
                                                                    )
                                                                }
                                                            />
                                                            {wpChanged && (
                                                                <ChangeFlag>
                                                                    changed
                                                                </ChangeFlag>
                                                            )}
                                                        </WeekDropdownCell>
                                                    </WkDropdownCol>
                                                )}
                                                {showTemplateCol && (
                                                    <WkDropdownCol className="wk-col-dropdown wk-col-dropdown-template">
                                                        <WeekDropdownCell>
                                                            <CustomDropdown
                                                                className="wk-grid-dropdown"
                                                                selection
                                                                search
                                                                multiple
                                                                selectOnBlur={false}
                                                                options={templates}
                                                                value={
                                                                    row.assignment
                                                                        .templateIds || []
                                                                }
                                                                placeholder="Select templates"
                                                                onChange={(
                                                                    _,
                                                                    { value },
                                                                ) =>
                                                                    updateAssignment(
                                                                        row.key,
                                                                        'templateIds',
                                                                        value || [],
                                                                    )
                                                                }
                                                            />
                                                        </WeekDropdownCell>
                                                    </WkDropdownCol>
                                                )}
                                                <QuickFillCell className="wk-quick-fill">
                                                    {row.isCustom ? (
                                                        <CopyBtn
                                                            type="button"
                                                            onClick={() =>
                                                                removeCustomRow(
                                                                    row.key,
                                                                )
                                                            }
                                                        >
                                                            Remove
                                                        </CopyBtn>
                                                    ) : (
                                                        renderQuickFillButton(
                                                            idx,
                                                            {
                                                                copyFrom:
                                                                    copyFromRow,
                                                            },
                                                        )
                                                    )}
                                                </QuickFillCell>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            <WeekGridToolbar
                                onFillAll={fillAllFromWeek1}
                                onClearAll={clearAllAssignments}
                                showCustomRange={showCustomRange}
                                onToggleCustomRange={() =>
                                    setShowCustomRange(v => !v)
                                }
                                customStart={customStart}
                                customEnd={customEnd}
                                onCustomStartChange={e =>
                                    setCustomStart(e.target.value)
                                }
                                onCustomEndChange={e =>
                                    setCustomEnd(e.target.value)
                                }
                                onAddCustomRange={handleAddCustomDateRange}
                                onCancelCustomRange={() => {
                                    setShowCustomRange(false)
                                    setCustomStart('')
                                    setCustomEnd('')
                                }}
                            />
                        </WeekGridWrap>

                        <h4 style={{ margin: '18px 0 8px', fontSize: '15px' }}>
                            What will be saved (preview)
                        </h4>
                        <PreviewStrip>
                            {weekRows.map((row, idx) => {
                                const prev = idx > 0 ? weekRows[idx - 1] : null
                                const willSave = isWeekRowComplete(row)
                                const wp = resolveWorkPlaceId(row)
                                const tpl = resolveTemplateIds(row)
                                const changed =
                                    willSave &&
                                    prev &&
                                    isWeekRowComplete(prev) &&
                                    (wp !== resolveWorkPlaceId(prev) ||
                                        !templateIdsEqual(
                                            tpl,
                                            resolveTemplateIds(prev),
                                        ))

                                return (
                                    <PreviewWk
                                        key={row.key}
                                        $changed={changed}
                                        $skipped={!willSave}
                                    >
                                        <div className="pw-label">
                                            {row.isCustom
                                                ? 'Custom'
                                                : `Wk ${row.weekOfMonth}`}{' '}
                                            ·{' '}
                                            {formatWeekDateRangeLabel(
                                                row.startDate,
                                                row.endDate,
                                            )}
                                        </div>
                                        <div className="pw-loc">
                                            {willSave
                                                ? findOptionText(workPlaces, wp)
                                                : 'Not saved'}
                                        </div>
                                        <div className="pw-tpl">
                                            {willSave
                                                ? findOptionsText(
                                                      templates,
                                                      tpl,
                                                  )
                                                : ''}
                                        </div>
                                    </PreviewWk>
                                )
                            })}
                        </PreviewStrip>
                    </RotationStepBody>
                </RotationStepCard>

                <RotationFooterCard>
                    <RotationStepHeader>
                        <RotationStepTitle>Review &amp; save</RotationStepTitle>
                        <RotationStepSubtitle>
                            Live check of what you&apos;re about to save.
                        </RotationStepSubtitle>
                    </RotationStepHeader>
                    <RotationStepBody>
                        <ValidationStrip $ok={validation.ok}>
                            {validation.ok ? (
                                <>
                                    ✓ {weeksToSave.length} week
                                    {weeksToSave.length === 1 ? '' : 's'} will
                                    be saved with auditor, shift, workplace, and
                                    template.{' '}
                                    {weekRows.length > weeksToSave.length
                                        ? `${weekRows.length - weeksToSave.length} row${
                                              weekRows.length -
                                                  weeksToSave.length ===
                                              1
                                                  ? ''
                                                  : 's'
                                          } empty — not saved. `
                                        : ''}
                                    {emailSummary}
                                </>
                            ) : (
                                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                    {validation.issues.map(issue => (
                                        <li key={issue}>{issue}</li>
                                    ))}
                                </ul>
                            )}
                        </ValidationStrip>

                        <SaveBtnRow>
                            <BtnGhost type="button" onClick={onCancel}>
                                Cancel
                            </BtnGhost>
                            <BtnSecondary
                                type="button"
                                disabled={loading || !validation.ok}
                                onClick={() => onSaveDraft?.()}
                            >
                                Save as draft
                            </BtnSecondary>
                            <BtnPrimary
                                type="button"
                                disabled={loading || !validation.ok}
                                onClick={() => onSave?.()}
                            >
                                {`Save Rotation (${weeksToSave.length} week${
                                    weeksToSave.length === 1 ? '' : 's'
                                }) →`}
                            </BtnPrimary>
                        </SaveBtnRow>
                    </RotationStepBody>
                </RotationFooterCard>
            </>
        )
    },
)

WeeklyRotationEntryForm.displayName = 'WeeklyRotationEntryForm'
